import {
  useNavigationDrawerState,
  useTheme,
} from '@backoffice-frontend/patterns'
import type { ReactNode } from 'react'
import { AreaContentHeader } from './AreaContentHeader'

/**
 * Color of the `attention`` frame drawn around the whole app.
 */
export type AttentionTone = 'accent' | 'critical'

const ATTENTION_BORDER_WIDTH = '8px'

type AppLayoutProps = {
  /** Frames the app in this tone. Omit to leave the frame off. */
  attention?: AttentionTone
  banner?: ReactNode
  navigation: ReactNode
  children: ReactNode
}

export const AppLayout = ({
  attention,
  banner,
  navigation,
  children,
}: AppLayoutProps) => {
  const theme = useTheme()
  const isNavigationOpen = useNavigationDrawerState()

  const attentionColor =
    attention === 'critical'
      ? theme.semantic.ColorCriticalDefault
      : theme.semantic.ColorAccentDefault

  return (
    // view
    <div
      css={{
        height: '100vh',
        display: 'grid',
      }}
    >
      {/* view-content */}
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...(attention && {
            border: `${ATTENTION_BORDER_WIDTH} solid ${attentionColor}`,
            background: attentionColor,
          }),
        }}
      >
        {/* Banner */}
        <div>{banner}</div>

        {/* content */}
        <div
          css={{
            display: 'flex',
            flex: 1,
            minHeight: 0, // needed to trigger overflow
            width: '100%',
            overflow: 'hidden',
            background: 'var(--color-background-default)',
            ...(attention && {
              borderRadius: 'var(--radius-large)',
            }),
          }}
        >
          {/* nav */}
          <div
            css={{
              overflowY: 'auto',
              boxSizing: 'border-box',
              zIndex: 1,
              display: 'flex',
              transition: 'width 200ms',
              background: theme.semantic.ColorSurfaceDefault,
              boxShadow: theme.shadows[2],

              [theme.breakpoints.up('md')]: {
                width: '80px',
                ...(isNavigationOpen && {
                  width: '280px',
                }),
              },
              [theme.breakpoints.down('md')]: {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'none',
                zIndex: 251,
                ...(isNavigationOpen && {
                  display: 'flex',
                }),
              },
            }}
          >
            {navigation}
          </div>
          {/* area */}
          <div
            css={{
              overflow: 'auto',
              boxSizing: 'border-box',
              flex: '1 1 0%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Portal for area TopBar */}
            <AreaContentHeader />

            {/* Actual area content */}
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
